import { Label } from '@hqo/ui-atoms';
import {
  Controller,
  Control,
  FieldErrors,
  FieldPath,
  FieldValues,
  UseFormSetValue,
  PathValue,
  Path,
} from 'react-hook-form';
import { DatePickerButton } from './date-picker-button';
import { DatePickerButtonProps } from './date-picker-button.types';
import { useValidationMessage } from 'components/create-visit-form-v2/hooks/use-validation-message.hook';

interface DatePickerFieldProps<TFieldValues extends FieldValues>
  extends Omit<DatePickerButtonProps, 'value' | 'onChange' | 'error'> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  required?: boolean;
  helperText?: string;
  fixedLabelHeight?: boolean;
  triggerValidation?: () => Promise<boolean | void>;
  setValue?: UseFormSetValue<TFieldValues>;
}

export const DatePickerField = <TFieldValues extends FieldValues>({
  name,
  control,
  errors,
  label,
  id,
  required = false,
  helperText,
  fixedLabelHeight = true,
  triggerValidation,
  setValue,
  ...datePickerProps
}: DatePickerFieldProps<TFieldValues>): JSX.Element => {
  const formatValidationMessage = useValidationMessage();
  const error = errors[name];
  const errorMessage = error?.message ? formatValidationMessage(error.message as string) : undefined;

  return (
    <div className="flex flex-col">
      {/* Label area - fixed height to align inputs */}
      {label && (
        <div className={`mb-1.5 flex items-end ${fixedLabelHeight ? 'h-7' : ''}`}>
          <Label htmlFor={id} className="gap-1">
            {label}
            {required && <span className="text-destructive">*</span>}
          </Label>
        </div>
      )}
      <Controller
        name={name}
        control={control}
        // eslint-disable-next-line react/jsx-no-bind
        render={({ field }) => (
          <DatePickerButton
            id={id}
            value={field.value || null}
            // eslint-disable-next-line react/jsx-no-bind
            onChange={async (date) => {
              // Use setValue with shouldValidate: false if provided to prevent triggering validation on all fields
              if (setValue) {
                setValue(name, date as PathValue<TFieldValues, Path<TFieldValues>>, {
                  shouldValidate: false,
                  shouldDirty: true,
                  shouldTouch: true,
                });
              } else {
                field.onChange(date);
              }

              // Trigger validation for cross-field validations if provided
              if (triggerValidation) {
                await triggerValidation();
              }
            }}
            error={errorMessage}
            label={label}
            {...datePickerProps}
          />
        )}
      />
      {/* Helper text area - show error or helper text */}
      {helperText && !errorMessage && (
        <div className="mt-1">
          <p className="text-xs text-muted-foreground break-words">{helperText}</p>
        </div>
      )}
    </div>
  );
};
